// components/AppShell.js
//
// The signed-in chrome, rebuilt to match the app.
//
// ── What this replaces ─────────────────────────────────────────────────────
//
// The old DashboardLayout carried three parallel navigations: a nine-item
// desktop sidebar, a hamburger drawer, and a seven-item bottom bar — each with
// a different subset of the same destinations. The app settled this argument a
// while ago (src/navigation/routes.js): three tabs, Invites / Create / You,
// with Explore and Communities kept reachable but unadvertised. This is that,
// on the web.
//
// The drawer is gone rather than fixed. It existed to hold the six items that
// did not fit the bottom bar, and those six items do not belong in a phone's
// primary navigation — Invoices, Upgrade and Guest lists live under You, the
// same as the app. (It was also invisible: a global `.mobile-menu { background:
// #ffffff !important }` in Welcome.scss painted it white on white. That rule is
// now scoped to the marketing page, but the drawer is not coming back.)
//
// ── Two layouts, not one stretched ─────────────────────────────────────────
//
// Phone: top bar with the wordmark and two actions, tab bar at the bottom.
// Desktop: a sidebar, because a 1440px window has horizontal room the phone
// does not, and a bottom bar there is three buttons marooned at the foot of
// the screen.
//
// DashboardLayout.js still exists and re-exports this, so the twelve pages
// that import it need no edit.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  FiBell,
  FiUser,
  FiFileText,
  FiTrendingUp,
  FiMail,
  FiList,
  FiSettings,
  FiLogOut,
} from 'react-icons/fi';
import {
  IoSparkles,
  IoSparklesOutline,
  IoAddCircle,
  IoPersonCircle,
  IoPersonCircleOutline,
} from 'react-icons/io5';
import { useAuth } from '../services/auth';
import supabase from '../services/supabase';
import CreateOptionsSheet from './CreateOptionsSheet';
import logotext from '../assets/logotext.png';
import logomark from '../assets/favicon.png';
import './AppShell.scss';

/**
 * Is this path the Home tab?
 *
 * /explore, /invitations and /communities all redirect to /home now, but a
 * link from an email or an old bookmark can still put one of them in the bar
 * for the instant before the redirect lands. Treating them as Home keeps the
 * tab from flickering.
 */
const isHomePath = (pathname) =>
  pathname === '/home' ||
  pathname === '/invitations' ||
  pathname === '/explore' ||
  pathname === '/greeting-cards';

export default function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const [userProfile, setUserProfile] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showCreateOptions, setShowCreateOptions] = useState(false);

  // No `loading` gate.
  //
  // The old layout rendered a full-page spinner until the profile row came
  // back, so every dashboard page flashed a spinner over content it already
  // had. The profile is only needed for an avatar and a username; the shell
  // renders without it and fills it in.
  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      return undefined;
    }
    let cancelled = false;
    supabase
      .from('users')
      .select('id, first_name, last_name, username, profile_photo_cdn_url')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (!cancelled) setUserProfile(data ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return undefined;
    }

    const fetchUnread = async () => {
      const { count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('read', false);
      setUnreadCount(count || 0);
    };
    fetchUnread();

    const channel = supabase
      .channel('shell-notif-count')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        fetchUnread,
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const profilePath = userProfile?.username ? `/${userProfile.username}` : '/dashboard';
  const onProfile =
    location.pathname === '/dashboard' ||
    (!!userProfile?.username && location.pathname === `/${userProfile.username}`);

  const createInvitation = useCallback(() => navigate('/create-invitation'), [navigate]);
  const createCard = useCallback(() => navigate('/create-greeting-card'), [navigate]);

  /** The three tabs, in bar order. Create does not navigate — it asks first. */
  const tabs = [
    {
      id: 'home',
      label: 'Invites',
      path: '/home',
      icon: IoSparklesOutline,
      activeIcon: IoSparkles,
      active: isHomePath(location.pathname),
    },
    {
      id: 'create',
      label: 'Create',
      icon: IoAddCircle,
      activeIcon: IoAddCircle,
      isAction: true,
      active: false,
    },
    {
      id: 'profile',
      label: 'You',
      path: profilePath,
      icon: IoPersonCircleOutline,
      activeIcon: IoPersonCircle,
      active: onProfile,
    },
  ];

  /**
   * Desktop-only destinations.
   *
   * These are the items the old bottom bar could not hold. On a phone they
   * live inside You, the same as the app; on desktop the sidebar has room, so
   * they are one click rather than two. Explore and Communities are not here:
   * they redirect to Home, matching `hidden: true` in the app's route table.
   */
  const sidebarExtras = [
    {
      id: 'cards',
      label: 'Cards',
      icon: FiMail,
      path: '/greeting-cards',
      active: location.pathname.startsWith('/greeting-card'),
    },
    {
      id: 'guests',
      label: 'Guest lists',
      icon: FiList,
      path: '/guest-lists',
      active: location.pathname.startsWith('/guest-list'),
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: FiBell,
      path: '/notifications',
      active: location.pathname === '/notifications',
      badge: unreadCount || null,
    },
    {
      id: 'invoices',
      label: 'Invoices',
      icon: FiFileText,
      path: '/invoices',
      active: location.pathname === '/invoices',
    },
    {
      id: 'upgrade',
      label: 'Upgrade',
      icon: FiTrendingUp,
      path: '/upgrade',
      active: location.pathname.startsWith('/upgrade'),
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: FiSettings,
      path: '/settings',
      active: location.pathname === '/settings',
    },
  ];

  const onTab = (tab) => {
    if (tab.isAction) {
      setShowCreateOptions(true);
      return;
    }
    navigate(tab.path);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const avatar = userProfile?.profile_photo_cdn_url;
  const displayName = [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ');

  return (
    <div className="shell">
      {/* The ground: one blurred image under everything, as in the app. */}
      <div className="shell-bg" aria-hidden="true">
        <img src="/images/events/5.jpg" alt="" />
        <div className="shell-bg-scrim" />
      </div>

      {/* ── Desktop sidebar ─────────────────────────────────────────────── */}
      <aside className="shell-sidebar">
        <Link to="/home" className="shell-sidebar-logo" aria-label="Invits, home">
          {/* The mark on the collapsed rail, the wordmark once it opens. */}
          <img className="shell-logo-mark" src={logomark} alt="" />
          <img className="shell-logo-full" src={logotext} alt="" />
        </Link>

        <nav className="shell-sidebar-nav" aria-label="Main">
          {tabs.map((tab) => {
            const Glyph = tab.active ? tab.activeIcon : tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                className={`shell-sidebar-item ${tab.active ? 'is-active' : ''} ${
                  tab.isAction ? 'is-create' : ''
                }`}
                aria-current={tab.active ? 'page' : undefined}
                onClick={() => onTab(tab)}
              >
                <span className="shell-sidebar-glyph">
                  <Glyph />
                </span>
                <span className="shell-sidebar-label">{tab.label}</span>
              </button>
            );
          })}

          <hr className="shell-sidebar-rule" />

          {sidebarExtras.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`shell-sidebar-item ${item.active ? 'is-active' : ''}`}
              aria-current={item.active ? 'page' : undefined}
              onClick={() => navigate(item.path)}
            >
              <span className="shell-sidebar-glyph">
                <item.icon />
                {item.badge ? (
                  <span className="shell-badge">{item.badge > 9 ? '9+' : item.badge}</span>
                ) : null}
              </span>
              <span className="shell-sidebar-label">{item.label}</span>
            </button>
          ))}
        </nav>

        {user && (
          <div className="shell-sidebar-foot">
            <button
              type="button"
              className="shell-sidebar-me"
              onClick={() => navigate(profilePath)}
            >
              <span className="shell-avatar">
                {avatar ? <img src={avatar} alt="" /> : <FiUser />}
              </span>
              <span className="shell-sidebar-me-text">
                <strong>{displayName || 'Your profile'}</strong>
                {userProfile?.username && <small>@{userProfile.username}</small>}
              </span>
            </button>
            <button type="button" className="shell-signout" onClick={handleSignOut}>
              <FiLogOut />
              <span className="shell-sidebar-label">Sign out</span>
            </button>
          </div>
        )}
      </aside>

      {/* ── Mobile header ───────────────────────────────────────────────── */}
      <header className="shell-topbar">
        <Link to="/home" className="shell-topbar-logo" aria-label="Invits, home">
          <img src={logotext} alt="Invits" />
        </Link>

        <div className="shell-topbar-actions">
          <button
            type="button"
            className="shell-icon-button"
            onClick={() => navigate('/notifications')}
            aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : 'Notifications'}
          >
            <FiBell size={20} />
            {unreadCount > 0 && (
              <span className="shell-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </button>

          {/* A button, and it goes somewhere.
              This was a bare <div> with no handler — the one control in the
              header that looked tappable and was not. */}
          <button
            type="button"
            className="shell-topbar-avatar"
            onClick={() => navigate(user ? profilePath : '/login')}
            aria-label={user ? 'Your profile' : 'Sign in'}
          >
            {avatar ? <img src={avatar} alt="" /> : <FiUser />}
          </button>
        </div>
      </header>

      <main className="shell-main">{children}</main>

      {/* ── Mobile tab bar ──────────────────────────────────────────────── */}
      <nav className="shell-tabbar" aria-label="Main">
        {tabs.map((tab) => {
          const Glyph = tab.active ? tab.activeIcon : tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              className={`shell-tab ${tab.active ? 'is-active' : ''} ${
                tab.isAction ? 'is-create' : ''
              }`}
              // Create opens a chooser rather than navigating, so it is a
              // button to a screen reader, not a tab. Announcing it as a tab
              // would promise navigation that does not happen.
              aria-label={tab.isAction ? 'Create, opens options' : tab.label}
              aria-current={tab.active ? 'page' : undefined}
              onClick={() => onTab(tab)}
            >
              <Glyph />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>

      <CreateOptionsSheet
        open={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
        onSelectInvitation={createInvitation}
        onSelectCard={createCard}
      />
    </div>
  );
}
